import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AdminSession, LoginRequest } from '@mall/shared';
import { useNavigate } from 'react-router-dom';
import { subscribeApiEvents } from '../../app/api';
import { useToast } from '../../app/ToastProvider';
import * as authApi from './api';

interface AuthContextValue {
  session: AdminSession | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  login: (payload: LoginRequest) => Promise<AdminSession>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [session, setSession] = useState<AdminSession | null>(null);
  const [status, setStatus] = useState<AuthContextValue['status']>('loading');

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.fetchMe();
      setSession(me ?? null);
      setStatus(me ? 'authenticated' : 'anonymous');
    } catch {
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return subscribeApiEvents((event) => {
      switch (event.type) {
        case 'unauthorized':
          setSession(null);
          setStatus('anonymous');
          if (window.location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
          break;
        case 'rate-limited':
          toast.warn(
            event.retryAfter
              ? `请求过于频繁，请 ${event.retryAfter}s 后重试`
              : '请求过于频繁，请稍后再试',
          );
          break;
        case 'error':
          if (event.error.name === 'NetworkError') {
            toast.error(event.error.message);
          }
          break;
      }
    });
  }, [navigate, toast]);

  const login = useCallback(
    async (payload: LoginRequest) => {
      const next = await authApi.login(payload);
      setSession(next);
      setStatus('authenticated');
      return next;
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setSession(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ session, status, login, logout, refresh }),
    [session, status, login, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
